// useActiveSession — PowerSync-backed hook for the
// `GET /capture/sessions/active` lookup used by the site-capture and
// building-capture screens during deep-link load. Replaces the REST
// hook in `apps/frontend/src/capture/hooks/use-active-session.ts`.
//
// Scope: the local SQLite mirror is already filtered by the
// `captures` sync rule to the calling user's sessions only — no
// cross-user leakage. The hook accepts a building OR site scope
// (XOR enforced at the type level, mirroring the REST hook's
// contract) and returns the SessionSummary row matching the scope,
// if one exists with `completed_at IS NULL`.
//
// Note on `otherActiveCount`: the REST endpoint's `otherActiveCount`
// field surfaces peer conflicts — i.e. the count of OTHER USERS
// actively capturing the same building/site (powering "another
// surveyor is in this building" warnings). The PowerSync `captures`
// stream is intentionally scoped per-user (`WHERE user_id =
// auth.user_id()` in powersync/sync-config.yaml), so the local
// mirror physically cannot answer that question. We always return
// 0 here and leave the field on the result type so callers compile.
// Peer-conflict detection is a separate concern that needs either a
// targeted REST call or a server-side push channel — tracked as a
// follow-up in the PowerSync rollout plan. (Codex P2 on PR #386.)
//
// SessionSummary shape: matches `@citycare/shared`'s SessionSummary
// — site/base/building summaries are JOINed in client-side from the
// local SQLite mirror, so the consumer receives the same hydrated
// envelope the REST endpoint produced.
//
// Sync-readiness gate: when the `captures` stream hasn't delivered
// its first sync, an empty result is NOT authoritative — the hook
// reports "loading" so a freshly installed device doesn't briefly
// render the StartCaptureCard before the user's existing session
// arrives.

import { useAllSyncStreamsHaveSynced, useQuery } from '@powersync/react'

import type { SessionSummary } from '@citycare/shared'

import { db } from './client'

export type ActiveSessionScope =
  | { readonly buildingId: string; readonly siteId?: never }
  | { readonly siteId: string; readonly buildingId?: never }

export type UseActiveSessionInput = ActiveSessionScope | null

export interface UseActiveSessionResult {
  readonly session: SessionSummary | null
  readonly otherActiveCount: number
  readonly isLoading: boolean
  readonly isFetching: boolean
  readonly error: Error | undefined
}

const IDLE_RESULT: UseActiveSessionResult = {
  session: null,
  otherActiveCount: 0,
  isLoading: false,
  isFetching: false,
  error: undefined,
}

interface SessionSqlRow {
  id: string
  site_id: string
  building_id: string | null
  user_id: string
  user_display_name: string
  started_at: string
  completed_at: string | null
  source_device_id: string | null
  synced_at: string | null
  sync_version: number
  created_at: string
  updated_at: string
  // JOIN columns
  site_name: string
  site_base_id: string
  site_base_name: string
  // LEFT-JOIN columns (nullable when buildingId is null)
  building_name: string | null
  building_floc: string | null
  building_status: string | null
}

function inputKey(input: UseActiveSessionInput): string | null {
  if (input === null) return null
  const buildingId = input.buildingId
  const siteId = input.siteId
  const hasBuilding = typeof buildingId === 'string' && buildingId !== ''
  const hasSite = typeof siteId === 'string' && siteId !== ''
  if (hasBuilding === hasSite) return null
  return hasBuilding ? `building:${buildingId ?? ''}` : `site:${siteId ?? ''}`
}

function projectSession(row: SessionSqlRow): SessionSummary {
  return {
    id: row.id,
    siteId: row.site_id,
    buildingId: row.building_id,
    userId: row.user_id,
    userDisplayName: row.user_display_name,
    startedAt: row.started_at,
    completedAt: row.completed_at,
    sourceDeviceId: row.source_device_id,
    syncedAt: row.synced_at,
    syncVersion: row.sync_version,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    site: {
      id: row.site_id,
      name: row.site_name,
      baseId: row.site_base_id,
      baseName: row.site_base_name,
    },
    building:
      row.building_id !== null && row.building_name !== null
        ? {
            id: row.building_id,
            name: row.building_name,
            floc: row.building_floc,
            // The pgEnum maps to TEXT in SQLite; consumers narrow it.
            status: (row.building_status ?? 'not_started') as SessionSummary['building'] extends {
              status: infer S
            }
              ? S
              : never,
          }
        : null,
  }
}

/**
 * Active capture session for a building or site scope. Returns the
 * caller's OWN session (sync rule already filters by user_id) — admin
 * impersonation paths are handled at the route layer, not here.
 */
export function useActiveSession(
  input: UseActiveSessionInput,
): UseActiveSessionResult {
  const key = inputKey(input)
  const isBuildingScope = input !== null && input.buildingId !== undefined
  const scopeId = (isBuildingScope ? input?.buildingId : input?.siteId) ?? ''

  // Building-scoped variant: building_id MUST match (LEFT JOIN keeps
  // all sessions but we filter by building_id explicitly).
  // Site-scoped variant: site_id matches AND building_id IS NULL
  // (otherwise we'd return the building-level session for the same
  // site, which is a different scope).
  const sessionWhere = isBuildingScope
    ? 'cs.building_id = ?'
    : 'cs.site_id = ? AND cs.building_id IS NULL'

  const sessionResult = useQuery<SessionSqlRow>(
    `SELECT
       cs.id, cs.site_id, cs.building_id, cs.user_id, cs.user_display_name,
       cs.started_at, cs.completed_at, cs.source_device_id, cs.synced_at,
       cs.sync_version, cs.created_at, cs.updated_at,
       s.name AS site_name,
       s.base_id AS site_base_id,
       bs.name AS site_base_name,
       b.name AS building_name,
       b.floc AS building_floc,
       b.status AS building_status
     FROM capture_sessions cs
     INNER JOIN sites s ON cs.site_id = s.id
     INNER JOIN bases bs ON s.base_id = bs.id
     LEFT JOIN buildings b ON cs.building_id = b.id
     WHERE cs.completed_at IS NULL
       AND ${sessionWhere}
     LIMIT 1`,
    key === null ? [''] : [scopeId],
  )

  // `otherActiveCount` cannot be derived from the local mirror — see
  // file header. We always return 0 here; peer-conflict detection is
  // a follow-up that needs server-side data.

  const capturesSynced = useAllSyncStreamsHaveSynced(db, [
    { name: 'captures', waitForStream: true },
  ])

  const sessionIsEmptyButUnsynced =
    sessionResult.data.length === 0 && !capturesSynced

  if (key === null) return IDLE_RESULT

  // Stale-data guard: every returned row's scope must match the
  // request. PowerSync may keep returning the prior route's session
  // briefly during navigation — suppress it.
  const sessionScopeMatches =
    sessionResult.data.length === 0 ||
    (isBuildingScope
      ? sessionResult.data[0]?.building_id === scopeId
      : sessionResult.data[0]?.site_id === scopeId &&
        sessionResult.data[0]?.building_id === null)
  const isStale = !sessionScopeMatches

  const isLoading =
    sessionResult.isLoading || isStale || sessionIsEmptyButUnsynced
  const isFetching = sessionResult.isFetching
  const error = sessionResult.error

  const session =
    !isStale && sessionResult.data.length > 0
      ? projectSession(sessionResult.data[0] as SessionSqlRow)
      : null

  return {
    session,
    // See file header: peer-conflict detection requires server data.
    otherActiveCount: 0,
    isLoading,
    isFetching,
    error,
  }
}
